import React from 'react';
import { SectionProps } from '../../types/section';

interface KPICardProps extends SectionProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary';
  accentStyle?: 'gradient' | 'corner' | 'shadow' | 'default';
  colorClass?: string;
  currency?: string;
  className?: string;
  loading?: boolean;
  onClick?: () => void;
  'aria-label'?: string;
  error?: string | null;
}

const   KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  icon,
  variant = 'default',
  accentStyle = 'default',
  colorClass,
  currency,
  className = '',
  loading = false,
  onClick,
  'aria-label': ariaLabel,
  sectionId,
  error,
  ...props
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'success':
        return 'kpi-card-success';
      case 'warning':
        return 'kpi-card-warning';
      case 'error':
        return 'kpi-card-error';
      case 'info':
        return 'kpi-card-info';
      case 'primary':
        return 'kpi-card-primary';
      default:
        return '';
    }
  };

  const cardClasses = [
    'kpi-card',
    getVariantClasses(),
    loading && 'loading-pulse',
    onClick && 'cursor-pointer',
    className
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (onClick && !loading) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && !loading && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  // Get accent color based on variant
  const getAccentColor = () => {
    switch (variant) {
      case 'success':
        return '#059669'; // Green-600
      case 'warning':
        return '#F59E0B'; // Amber-500
      case 'error':
        return '#DC2626'; // Red-600
      case 'info':
        return '#0891B2'; // Cyan-600
      case 'primary':
        return '#7C3AED'; // Violet-600
      default:
        return '#6B7280'; // Gray-500
    }
  };

  // Get accent color class for Tailwind
  const getAccentColorClass = () => {
    switch (variant) {
      case 'success':
        return 'status-success';
      case 'warning':
        return 'status-warning';
      case 'error':
        return 'status-error';
      case 'info':
        return 'status-info';
      case 'primary':
        return 'primary-600';
      default:
        return 'border-interactive';
    }
  };

  // Render different accent styles
  const renderAccentStyle = () => {
    const accentColor = getAccentColor();
    
    switch (accentStyle) {
      case 'gradient':
        return (
          <div 
            className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
            style={{
              background: `linear-gradient(to bottom, ${accentColor}, ${accentColor}50)`
            }}
          />
        );
      
      case 'corner':
        return (
          <>
            <div 
              className="absolute top-0 left-0 w-12 h-12 rounded-br-full"
              style={{ 
                backgroundColor: accentColor,
                opacity: 0.1 
              }}
            />
            <div 
              className="absolute top-2 left-2 w-2 h-2 rounded-full"
              style={{ backgroundColor: accentColor }}
            />
          </>
        );
      
      case 'shadow':
        return (
          <div 
            className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r-full"
            style={{
              backgroundColor: accentColor,
              boxShadow: `2px 0 8px ${accentColor}80`
            }}
          />
        );
      
      default:
        return null;
    }
  };
    
  // Base card styles
  const baseClasses = 'kpi rounded-lg p-4 relative overflow-hidden border';
  const baseStyles = accentStyle === 'default' 
    ? { ...getBackgroundStyles() } 
    : { backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-default)',borderRadius: 'var(--card-border-radius)' };

  return (
    <div 
      className={`${baseClasses} ${loading ? 'loading-pulse' : ''} ${onClick ? 'cursor-pointer hover:shadow-md transition-all' : ''} ${className}`}
      style={baseStyles}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel || `${title}: ${value}${currency ? ` ${currency}` : ''}`}
      // aria-label={ariaLabel || `${title}: ${value}${currency ? ` ${currency}` : ''}`}
      aria-busy={loading}
      data-section-id={sectionId}
      data-error={error || undefined}
      {...props}
    >
      {/* Accent styling */}
      {renderAccentStyle()}
      
      {/* Content wrapper with proper spacing for accent styles */}
      <div className={accentStyle === 'gradient' || accentStyle === 'shadow' ? 'pl-3' : ''}>
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-text-secondary">
            {title}
            {loading && <span className="sr-only"> (در حال بارگذاری)</span>}
          </h3>
          {icon && accentStyle !== 'corner' && (
            <div className="opacity-20" style={{ color: getAccentColor() }}>
              {icon}
            </div>
          )}
        </div>
        
        {/* Main value */}
        {loading ? (
          <div className="loading-skeleton h-8 w-24 rounded"></div>
        ) : (
          <div className="text-2xl font-bold text-text-primary">
            {typeof value === 'number' ? value.toLocaleString() : value}
            {/* {currency && <span className="text-lg ml-1">{currency}</span>} */}
          </div>
        )}
        
        {/* Subtitle */}
        {subtitle && (
          <div className="mt-1">
            {loading ? (
              <div className="loading-skeleton h-4 w-20 rounded"></div>
            ) : (
              <p className="text-xs text-text-tertiary">{subtitle}</p>
            )}
          </div>
        )}
      </div>

      {/* Loading indicator for screen readers */}
      {loading && (
        <div className="sr-only" aria-live="polite">
          در حال بارگذاری اطلاعات
        </div>
      )}
    </div>
  );

  // Helper function for default style
  function getBackgroundStyles() {
    const color = getAccentColor();
    if (variant === 'default') {
      return {};
    }
    return {
      backgroundColor: `${color}10`, // 10% opacity
      borderColor: color
    };
  }
};

export default KPICard;